import React, { useState, useEffect, useCallback } from 'react';
import {
  Ship, Package, BarChart3, FileText, Activity,
  Calendar, Download, Printer, RefreshCw, Search,
  ChevronUp, ChevronDown, TrendingUp, Clock, Eye,
  Anchor, Wind, BookOpen, Filter
} from 'lucide-react';
import apiClient from '../services/api';

// ─────────────────────────────────────────────────────────────────────────────
// Report types  (mirror FinancialReports REPORT_TYPES structure exactly)
// ─────────────────────────────────────────────────────────────────────────────
const REPORT_TYPES = [
  { key: 'job_list',          label: 'Job List',          icon: FileText,   color: '#0f766e' },
  { key: 'revenue_summary',   label: 'Revenue Summary',   icon: TrendingUp, color: '#1d4ed8' },
  { key: 'revenue_by_type',   label: 'Revenue by Type',   icon: BarChart3,  color: '#7c3aed' },
  { key: 'container_report',  label: 'Containers',        icon: Package,    color: '#b45309' },
  { key: 'cargo_report',      label: 'Cargo Details',     icon: Package,    color: '#be123c' },
  { key: 'shipment_schedule', label: 'Shipment Schedule', icon: Calendar,   color: '#0369a1' },
  { key: 'job_status',        label: 'Job Status',        icon: Activity,   color: '#166534' },
  { key: 'job_details',       label: 'Job Detail',        icon: Eye,        color: '#9f1239' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const fmtDate = v => {
  if (!v) return '—';
  const d = new Date(v + 'T00:00:00');
  return isNaN(d) ? v : `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
};
const fmtNumRaw = (v, dec = 2) => {
  const n = parseFloat(v) || 0;
  return n.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
};
const fmtNum = (v, dec = 2) => {
  const n = parseFloat(v);
  if (isNaN(n) || n === 0) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
};

// ─────────────────────────────────────────────────────────────────────────────
// Shared style objects  (identical to FinancialReports)
// ─────────────────────────────────────────────────────────────────────────────
const tableStyle = {
  width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem',
  background: '#fff', borderRadius: 8, overflow: 'hidden',
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
};
const thStyle = {
  padding: '9px 12px', fontWeight: '700', fontSize: '0.72rem',
  color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase',
  background: '#1e293b', whiteSpace: 'nowrap',
  fontFamily: "'Libre Baskerville',Georgia,serif",
  cursor: 'pointer', userSelect: 'none',
};
const tdStyle = {
  padding: '7px 12px', borderBottom: '1px solid #f1f5f9',
  verticalAlign: 'middle', fontFamily: "'Libre Baskerville',Georgia,serif",
};
const lblStyle = {
  display: 'block', fontSize: '0.74rem', fontWeight: '700',
  color: '#374151', marginBottom: 4, letterSpacing: '0.03em',
  fontFamily: "'Libre Baskerville',Georgia,serif",
};
const inpStyle = {
  width: '100%', padding: '8px 10px', border: '1px solid #d1d5db',
  borderRadius: 6, fontSize: '0.83rem', background: '#fff',
  boxSizing: 'border-box', fontFamily: "'Libre Baskerville',Georgia,serif",
};
const btnStyle = {
  display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px',
  border: '1px solid #e2e8f0', borderRadius: 7, background: '#fff',
  color: '#374151', cursor: 'pointer', fontSize: '0.8rem',
  fontFamily: "'Libre Baskerville',Georgia,serif", fontWeight: '600',
};

// ─────────────────────────────────────────────────────────────────────────────
// Small reusable atoms
// ─────────────────────────────────────────────────────────────────────────────
const Spinner = () => (
  <div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin .7s linear infinite', display: 'inline-block' }} />
);

const Stat = ({ label, value, color }) => (
  <div style={{ textAlign: 'right' }}>
    <div style={{ fontSize: '0.66rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'Libre Baskerville',Georgia,serif" }}>{label}</div>
    <div style={{ fontFamily: "'Courier Prime','Courier New',monospace", color, fontWeight: '700', fontSize: '0.9rem' }}>{value}</div>
  </div>
);

const ModeTag = ({ mode }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 3,
    padding: '2px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: '700',
    background: mode === 'Sea' ? '#dbeafe' : '#fef3c7',
    color: mode === 'Sea' ? '#1e3a8a' : '#92400e',
  }}>
    {mode === 'Sea' ? <Anchor size={9} /> : <Wind size={9} />}{mode || '—'}
  </span>
);

const StatusTag = ({ status }) => (
  <span style={{
    padding: '2px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: '700',
    background: status === 'Closed' ? '#f1f5f9' : '#dcfce7',
    color: status === 'Closed' ? '#475569' : '#166534',
  }}>{status || '—'}</span>
);

const AmtCell = ({ value, color }) => {
  const n = parseFloat(value) || 0;
  if (n === 0) return <span style={{ color: '#cbd5e1' }}>—</span>;
  return (
    <span style={{ fontFamily: "'Courier Prime','Courier New',monospace", color: color || '#1e293b', fontWeight: '600', fontSize: '0.82rem' }}>
      {fmtNumRaw(Math.abs(n))}
    </span>
  );
};

const ProfitCell = ({ value }) => {
  const n = parseFloat(value) || 0;
  return (
    <span style={{ fontFamily: "'Courier Prime','Courier New',monospace", fontWeight: '700', fontSize: '0.82rem', color: n >= 0 ? '#166534' : '#dc2626' }}>
      {fmtNumRaw(Math.abs(n))} {n >= 0 ? '▲' : '▼'}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Sort hook
// ─────────────────────────────────────────────────────────────────────────────
const useSort = (rows) => {
  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState('desc');

  const toggle = key => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const sorted = sortKey ? [...rows].sort((a, b) => {
    const av = a[sortKey] ?? '', bv = b[sortKey] ?? '';
    const an = parseFloat(av), bn = parseFloat(bv);
    const cmp = !isNaN(an) && !isNaN(bn) ? an - bn : String(av).localeCompare(String(bv));
    return sortDir === 'asc' ? cmp : -cmp;
  }) : rows;

  const SortIcon = ({ k }) => (
    sortKey === k
      ? (sortDir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />)
      : <ChevronUp size={11} style={{ opacity: .25 }} />
  );

  return { sorted, toggle, SortIcon };
};

const Th = ({ k, label, align, toggle, SortIcon }) => (
  <th style={{ ...thStyle, textAlign: align || 'left' }} onClick={() => toggle?.(k)}>
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      {label}{SortIcon && <SortIcon k={k} />}
    </span>
  </th>
);

const TRow = ({ children, i }) => (
  <tr style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}
    onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafafa'}>
    {children}
  </tr>
);

// ─────────────────────────────────────────────────────────────────────────────
// 1. JOB LIST
// ─────────────────────────────────────────────────────────────────────────────
const JobListTable = ({ rows, searchTerm }) => {
  const { sorted, toggle, SortIcon } = useSort(rows);
  const q = searchTerm.toLowerCase();
  const filtered = q
    ? sorted.filter(r => ['JobCode','InvoiceTo','Consigner','LoadPort','DestPort','BLNo','JobType'].some(k => String(r[k] ?? '').toLowerCase().includes(q)))
    : sorted;

  return (
    <div style={{ overflow: 'auto' }}>
      <table style={tableStyle}>
        <thead>
          <tr style={{ background: '#1e293b' }}>
            {[
              ['JobCode','Job Code'],['JobDate','Date'],['JobType','Type'],['Mode','Mode'],
              ['InvoiceTo','Customer'],['Consigner','Consigner'],['LoadPort','Load Port'],
              ['DestPort','Dest Port'],['BLNo','MBL/MAWB'],['HBLNo','HBL/HAWB'],
              ['SailingDate','Sailing'],['ArrivalDate','Arrival'],['DeliveryDate','Delivery'],
              ['GrossWeight','Gross Wt',true],['HandledBy','Handler'],['Status','Status'],
            ].map(([k, label, right]) => (
              <Th key={k} k={k} label={label} align={right ? 'right' : 'left'} toggle={toggle} SortIcon={SortIcon} />
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.map((r, i) => (
            <TRow key={r.JobID} i={i}>
              <td style={tdStyle}><span style={{ fontFamily: 'monospace', color: '#0f766e', fontWeight: '700', fontSize: '0.82rem' }}>{r.JobCode}</span></td>
              <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#475569' }}>{fmtDate(r.JobDate)}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem' }}>{r.JobType || '—'}</td>
              <td style={tdStyle}><ModeTag mode={r.Mode} /></td>
              <td style={{ ...tdStyle, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.InvoiceTo || '—'}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#64748b', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.Consigner || '—'}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem' }}>{r.LoadPort || '—'}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem' }}>{r.DestPort || '—'}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.78rem', color: '#1d4ed8' }}>{r.BLNo || '—'}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.78rem', color: '#64748b' }}>{r.HBLNo || '—'}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#475569' }}>{fmtDate(r.SailingDate)}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#475569' }}>{fmtDate(r.ArrivalDate)}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#475569' }}>{fmtDate(r.DeliveryDate)}</td>
              <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={r.GrossWeight} /></td>
              <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#64748b' }}>{r.HandledBy || '—'}</td>
              <td style={tdStyle}><StatusTag status={r.Status} /></td>
            </TRow>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background: '#0f172a' }}>
            <td style={{ ...tdStyle, color: '#94a3b8', fontSize: '0.75rem', padding: '8px 12px' }} colSpan={3}>
              {filtered.length} jobs
            </td>
            <td style={{ ...tdStyle, color: '#fbbf24', fontSize: '0.75rem' }}>
              Sea: {filtered.filter(r => r.Mode === 'Sea').length} / Air: {filtered.filter(r => r.Mode === 'Air').length}
            </td>
            <td colSpan={12} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// 2. REVENUE SUMMARY
// ─────────────────────────────────────────────────────────────────────────────
const RevenueSummaryTable = ({ rows, summary, searchTerm }) => {
  const { sorted, toggle, SortIcon } = useSort(rows);
  const q = searchTerm.toLowerCase();
  const filtered = q ? sorted.filter(r => ['JobCode','InvoiceTo','BLNo'].some(k => String(r[k]??'').toLowerCase().includes(q))) : sorted;

  return (
    <div style={{ overflow: 'auto' }}>
      <table style={tableStyle}>
        <thead>
          <tr style={{ background: '#1e293b' }}>
            {[
              ['JobCode','Job Code'],['JobDate','Date'],['JobType','Type'],['Mode',''],
              ['InvoiceTo','Customer'],['LoadPort','Load'],['DestPort','Dest'],['BLNo','BL/AWB'],
              ['RevenueLines','Lines',true],['TotalSales','Sales',true],
              ['TotalCost','Cost',true],['GrossProfit','Profit',true],
              ['TotalTax','Tax',true],['Status',''],
            ].map(([k, label, right]) => (
              <Th key={k} k={k} label={label} align={right ? 'right' : 'left'} toggle={toggle} SortIcon={SortIcon} />
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.map((r, i) => (
            <TRow key={r.JobID} i={i}>
              <td style={tdStyle}><span style={{ fontFamily: 'monospace', color: '#0f766e', fontWeight: '700', fontSize: '0.82rem' }}>{r.JobCode}</span></td>
              <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#475569' }}>{fmtDate(r.JobDate)}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem' }}>{r.JobType || '—'}</td>
              <td style={tdStyle}><ModeTag mode={r.Mode} /></td>
              <td style={{ ...tdStyle, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.InvoiceTo || '—'}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem' }}>{r.LoadPort || '—'}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem' }}>{r.DestPort || '—'}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.78rem', color: '#1d4ed8' }}>{r.BLNo || '—'}</td>
              <td style={{ ...tdStyle, textAlign: 'right', color: '#64748b' }}>{r.RevenueLines}</td>
              <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={r.TotalSales} color="#1d4ed8" /></td>
              <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={r.TotalCost} color="#dc2626" /></td>
              <td style={{ ...tdStyle, textAlign: 'right' }}><ProfitCell value={r.GrossProfit} /></td>
              <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={r.TotalTax} color="#7c3aed" /></td>
              <td style={tdStyle}><StatusTag status={r.Status} /></td>
            </TRow>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background: '#0f172a' }}>
            <td colSpan={9} style={{ ...tdStyle, color: '#94a3b8', fontSize: '0.75rem', padding: '8px 12px' }}>{filtered.length} jobs</td>
            <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#93c5fd', fontWeight: '700' }}>{fmtNumRaw(filtered.reduce((s,r)=>s+(parseFloat(r.TotalSales)||0),0))}</td>
            <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#fca5a5', fontWeight: '700' }}>{fmtNumRaw(filtered.reduce((s,r)=>s+(parseFloat(r.TotalCost)||0),0))}</td>
            <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#86efac', fontWeight: '700' }}>{fmtNumRaw(filtered.reduce((s,r)=>s+(parseFloat(r.GrossProfit)||0),0))}</td>
            <td colSpan={2} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// 3. REVENUE BY TYPE
// ─────────────────────────────────────────────────────────────────────────────
const RevenueByTypeTable = ({ rows }) => {
  const { sorted, toggle, SortIcon } = useSort(rows);
  const maxSales = Math.max(...sorted.map(r => parseFloat(r.TotalSalesHome) || 0), 1);

  return (
    <div style={{ overflow: 'auto' }}>
      <table style={tableStyle}>
        <thead>
          <tr style={{ background: '#1e293b' }}>
            {[
              ['RevenueType','Revenue Type'],['RevenueCode','Code'],
              ['JobCount','Jobs',true],['TotalQty','Qty',true],
              ['TotalSalesHome','Sales',true],['TotalProvisionHome','Provision',true],
              ['TotalCost','Cost',true],['TotalTaxAmt','Tax',true],
              ['GrossProfit','Profit',true],['MarginPct','Margin%',true],
            ].map(([k, label, right]) => (
              <Th key={k} k={k} label={label} align={right ? 'right' : 'left'} toggle={toggle} SortIcon={SortIcon} />
            ))}
            <th style={{ ...thStyle, cursor: 'default' }}>Chart</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => {
            const barW = Math.max(4, Math.round((parseFloat(r.TotalSalesHome) || 0) / maxSales * 100));
            return (
              <TRow key={r.RevenueTypeID || i} i={i}>
                <td style={{ ...tdStyle, fontWeight: '600' }}>{r.RevenueType || 'Unassigned'}</td>
                <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.78rem', color: '#64748b' }}>{r.RevenueCode || '—'}</td>
                <td style={{ ...tdStyle, textAlign: 'right', color: '#64748b' }}>{r.JobCount}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={r.TotalQty} /></td>
                <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={r.TotalSalesHome} color="#1d4ed8" /></td>
                <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={r.TotalProvisionHome} /></td>
                <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={r.TotalCost} color="#dc2626" /></td>
                <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={r.TotalTaxAmt} color="#7c3aed" /></td>
                <td style={{ ...tdStyle, textAlign: 'right' }}><ProfitCell value={r.GrossProfit} /></td>
                <td style={{ ...tdStyle, textAlign: 'right', color: parseFloat(r.MarginPct) >= 0 ? '#166534' : '#dc2626', fontWeight: '700', fontFamily: 'monospace', fontSize: '0.82rem' }}>
                  {fmtNumRaw(r.MarginPct, 1)}%
                </td>
                <td style={{ ...tdStyle, minWidth: 120 }}>
                  <div style={{ background: '#f1f5f9', borderRadius: 4, height: 10 }}>
                    <div style={{ width: `${barW}%`, background: '#1d4ed8', height: '100%', borderRadius: 4 }} />
                  </div>
                </td>
              </TRow>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ background: '#0f172a' }}>
            <td style={{ ...tdStyle, color: '#94a3b8', fontSize: '0.75rem', padding: '8px 12px' }}>TOTAL</td>
            <td />
            <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#e2e8f0', fontWeight: '700' }}>{sorted.reduce((s,r)=>s+(parseInt(r.JobCount)||0),0)}</td>
            <td />
            <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#93c5fd', fontWeight: '700' }}>{fmtNumRaw(sorted.reduce((s,r)=>s+(parseFloat(r.TotalSalesHome)||0),0))}</td>
            <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#e2e8f0', fontWeight: '700' }}>{fmtNumRaw(sorted.reduce((s,r)=>s+(parseFloat(r.TotalProvisionHome)||0),0))}</td>
            <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#fca5a5', fontWeight: '700' }}>{fmtNumRaw(sorted.reduce((s,r)=>s+(parseFloat(r.TotalCost)||0),0))}</td>
            <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#c4b5fd', fontWeight: '700' }}>{fmtNumRaw(sorted.reduce((s,r)=>s+(parseFloat(r.TotalTaxAmt)||0),0))}</td>
            <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#86efac', fontWeight: '700' }}>{fmtNumRaw(sorted.reduce((s,r)=>s+(parseFloat(r.GrossProfit)||0),0))}</td>
            <td colSpan={2} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// 4. CONTAINER REPORT
// ─────────────────────────────────────────────────────────────────────────────
const ContainerTable = ({ rows, searchTerm }) => {
  const { sorted, toggle, SortIcon } = useSort(rows);
  const q = searchTerm.toLowerCase();
  const filtered = q ? sorted.filter(r => ['JobCode','ContainerNo','SealNo','InvoiceTo','ContainerType'].some(k => String(r[k]??'').toLowerCase().includes(q))) : sorted;

  return (
    <div style={{ overflow: 'auto' }}>
      <table style={tableStyle}>
        <thead>
          <tr style={{ background: '#1e293b' }}>
            {[
              ['JobCode','Job Code'],['JobDate','Date'],['JobType','Type'],
              ['InvoiceTo','Customer'],['LoadPort','Load Port'],['DestPort','Dest Port'],
              ['MBL','MBL'],['Vessel','Vessel'],['SailingDate','Sailing'],
              ['ContainerType','Ctr Type'],['ContainerNo','Container No'],
              ['SealNo','Seal No'],['ContainerDesc','Description'],['Status',''],
            ].map(([k, label]) => (
              <Th key={k} k={k} label={label} toggle={toggle} SortIcon={SortIcon} />
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.map((r, i) => (
            <TRow key={`${r.JobID}-${i}`} i={i}>
              <td style={tdStyle}><span style={{ fontFamily: 'monospace', color: '#0f766e', fontWeight: '700', fontSize: '0.82rem' }}>{r.JobCode}</span></td>
              <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#475569' }}>{fmtDate(r.JobDate)}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem' }}>{r.JobType || '—'}</td>
              <td style={{ ...tdStyle, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.InvoiceTo || '—'}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem' }}>{r.LoadPort || '—'}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem' }}>{r.DestPort || '—'}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.78rem', color: '#1d4ed8' }}>{r.MBL || '—'}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem' }}>{r.Vessel || '—'}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#475569' }}>{fmtDate(r.SailingDate)}</td>
              <td style={tdStyle}>
                <span style={{ background: '#dbeafe', color: '#1e3a8a', padding: '2px 8px', borderRadius: 8, fontSize: '0.72rem', fontWeight: '700' }}>
                  {r.ContainerType || '—'}
                </span>
              </td>
              <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: '700', color: '#b45309', fontSize: '0.85rem' }}>{r.ContainerNo || '—'}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace', color: '#64748b', fontSize: '0.8rem' }}>{r.SealNo || '—'}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#64748b', maxWidth: 160 }}>{r.ContainerDesc || '—'}</td>
              <td style={tdStyle}><StatusTag status={r.Status} /></td>
            </TRow>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background: '#0f172a' }}>
            <td colSpan={14} style={{ ...tdStyle, color: '#94a3b8', fontSize: '0.75rem', padding: '8px 12px' }}>
              {filtered.length} containers &nbsp;·&nbsp; {new Set(filtered.map(r => r.JobID)).size} jobs
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// 5. CARGO REPORT
// ─────────────────────────────────────────────────────────────────────────────
const CargoTable = ({ rows, searchTerm }) => {
  const { sorted, toggle, SortIcon } = useSort(rows);
  const q = searchTerm.toLowerCase();
  const filtered = q ? sorted.filter(r => ['JobCode','InvoiceTo','Consigner','Mark','CargoDescription'].some(k => String(r[k]??'').toLowerCase().includes(q))) : sorted;

  return (
    <div style={{ overflow: 'auto' }}>
      <table style={tableStyle}>
        <thead>
          <tr style={{ background: '#1e293b' }}>
            {[
              ['JobCode','Job Code'],['JobDate','Date'],['JobType','Type'],
              ['InvoiceTo','Customer'],['Consigner','Consigner'],
              ['LoadPort','Load Port'],['DestPort','Dest Port'],['BLNo','BL/AWB'],
              ['Mark','Marks'],['CargoDescription','Description'],
              ['Packages','Pkgs',true],['Weight','Weight',true],
              ['Volume','Volume',true],['GrossWeight','Gross Wt',true],
            ].map(([k, label, right]) => (
              <Th key={k} k={k} label={label} align={right ? 'right' : 'left'} toggle={toggle} SortIcon={SortIcon} />
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.map((r, i) => (
            <TRow key={`${r.JobID}-${i}`} i={i}>
              <td style={tdStyle}><span style={{ fontFamily: 'monospace', color: '#0f766e', fontWeight: '700', fontSize: '0.82rem' }}>{r.JobCode}</span></td>
              <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#475569' }}>{fmtDate(r.JobDate)}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem' }}>{r.JobType || '—'}</td>
              <td style={{ ...tdStyle, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.InvoiceTo || '—'}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#64748b', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.Consigner || '—'}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem' }}>{r.LoadPort || '—'}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem' }}>{r.DestPort || '—'}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.78rem', color: '#1d4ed8' }}>{r.BLNo || '—'}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.78rem', color: '#b45309' }}>{r.Mark || '—'}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#475569', maxWidth: 180 }}>{r.CargoDescription || '—'}</td>
              <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={r.Packages} /></td>
              <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={r.Weight} /></td>
              <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={r.Volume} /></td>
              <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={r.GrossWeight} /></td>
            </TRow>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background: '#0f172a' }}>
            <td colSpan={10} style={{ ...tdStyle, color: '#94a3b8', fontSize: '0.75rem', padding: '8px 12px' }}>
              {filtered.length} lines &nbsp;·&nbsp; {new Set(filtered.map(r => r.JobID)).size} jobs
            </td>
            <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#fbbf24', fontWeight: '700' }}>
              {fmtNumRaw(filtered.reduce((s, r) => s + (parseFloat(r.Packages) || 0), 0), 3)}
            </td>
            <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#e2e8f0', fontWeight: '700' }}>
              {fmtNumRaw(filtered.reduce((s, r) => s + (parseFloat(r.Weight) || 0), 0), 3)}
            </td>
            <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#e2e8f0', fontWeight: '700' }}>
              {fmtNumRaw(filtered.reduce((s, r) => s + (parseFloat(r.Volume) || 0), 0), 3)}
            </td>
            <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#e2e8f0', fontWeight: '700' }}>
              {fmtNumRaw(filtered.reduce((s, r) => s + (parseFloat(r.GrossWeight) || 0), 0), 3)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// 6. SHIPMENT SCHEDULE
// ─────────────────────────────────────────────────────────────────────────────
const ShipmentScheduleTable = ({ rows, searchTerm }) => {
  const { sorted, toggle, SortIcon } = useSort(rows);
  const q = searchTerm.toLowerCase();
  const filtered = q ? sorted.filter(r => ['JobCode','InvoiceTo','BLNo','Vessel'].some(k => String(r[k]??'').toLowerCase().includes(q))) : sorted;
  const today = new Date().toISOString().slice(0, 10);

  const dateColor = dateStr => {
    if (!dateStr) return '#94a3b8';
    const diff = Math.ceil((new Date(dateStr) - new Date(today)) / 86400000);
    if (diff < 0) return '#64748b';
    if (diff <= 3) return '#dc2626';
    if (diff <= 7) return '#d97706';
    return '#166534';
  };

  return (
    <div style={{ overflow: 'auto' }}>
      <table style={tableStyle}>
        <thead>
          <tr style={{ background: '#1e293b' }}>
            {[
              ['JobCode','Job Code'],['JobDate','Job Date'],['Mode',''],
              ['InvoiceTo','Customer'],['BLNo','BL/AWB'],
              ['Vessel','Vessel/Flight'],['LoadPort','Load Port'],['DestPort','Dest Port'],
              ['DeliveryPlace','Delivery Place'],['BLDate','BL Date'],
              ['SailingDate','Sailing'],['ArrivalDate','Arrival'],
              ['CollectionDate','Collection'],['DeliveryDate','Delivery'],
              ['DaysAtSea','Sea Days',true],['TotalDays','Total Days',true],
              ['Transporter','Transporter'],['HandledBy','Handler'],['Status',''],
            ].map(([k, label, right]) => (
              <Th key={k} k={k} label={label} align={right ? 'right' : 'left'} toggle={toggle} SortIcon={SortIcon} />
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.map((r, i) => (
            <TRow key={r.JobID} i={i}>
              <td style={tdStyle}><span style={{ fontFamily: 'monospace', color: '#0f766e', fontWeight: '700', fontSize: '0.82rem' }}>{r.JobCode}</span></td>
              <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#475569' }}>{fmtDate(r.JobDate)}</td>
              <td style={tdStyle}><ModeTag mode={r.Mode} /></td>
              <td style={{ ...tdStyle, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.InvoiceTo || '—'}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.78rem', color: '#1d4ed8' }}>{r.BLNo || '—'}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem' }}>{r.Vessel || r.Airline || '—'}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem' }}>{r.LoadPort || '—'}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem' }}>{r.DestPort || '—'}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#64748b' }}>{r.DeliveryPlace || '—'}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#64748b' }}>{fmtDate(r.BLDate)}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem' }}>
                {r.SailingDate ? <span style={{ color: dateColor(r.SailingDate), fontWeight: '600' }}>{fmtDate(r.SailingDate)}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}
              </td>
              <td style={{ ...tdStyle, fontSize: '0.78rem' }}>
                {r.ArrivalDate ? <span style={{ color: dateColor(r.ArrivalDate), fontWeight: '600' }}>{fmtDate(r.ArrivalDate)}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}
              </td>
              <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#475569' }}>{fmtDate(r.CollectionDate)}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem' }}>
                {r.DeliveryDate ? <span style={{ color: dateColor(r.DeliveryDate), fontWeight: '600' }}>{fmtDate(r.DeliveryDate)}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}
              </td>
              <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.82rem', color: '#0369a1', fontWeight: '600' }}>{r.DaysAtSea ?? '—'}</td>
              <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.82rem', color: '#64748b' }}>{r.TotalDays ?? '—'}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#64748b' }}>{r.Transporter || '—'}</td>
              <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#64748b' }}>{r.HandledBy || '—'}</td>
              <td style={tdStyle}><StatusTag status={r.Status} /></td>
            </TRow>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// 7. JOB STATUS
// ─────────────────────────────────────────────────────────────────────────────
const JobStatusTable = ({ rows }) => (
  <div style={{ overflow: 'auto' }}>
    <table style={tableStyle}>
      <thead>
        <tr style={{ background: '#1e293b' }}>
          {['Job Type','Mode','Job Mode','Status','Job Count','Invoiced','Total Sales','Total Cost','Gross Profit','Avg Days'].map(h => (
            <th key={h} style={{ ...thStyle, cursor: 'default', textAlign: ['Job Count','Invoiced','Total Sales','Total Cost','Gross Profit','Avg Days'].includes(h) ? 'right' : 'left' }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <TRow key={i} i={i}>
            <td style={{ ...tdStyle, fontWeight: '600' }}>{r.JobType || '—'}</td>
            <td style={tdStyle}><ModeTag mode={r.Mode} /></td>
            <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#64748b' }}>{r.JobMode || '—'}</td>
            <td style={tdStyle}><StatusTag status={r.Status} /></td>
            <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', fontWeight: '700', fontSize: '0.9rem', color: '#0369a1' }}>{r.JobCount}</td>
            <td style={{ ...tdStyle, textAlign: 'right', color: '#64748b', fontFamily: 'monospace' }}>{r.InvoicedCount}</td>
            <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={r.TotalSales} color="#1d4ed8" /></td>
            <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={r.TotalCost} color="#dc2626" /></td>
            <td style={{ ...tdStyle, textAlign: 'right' }}><ProfitCell value={r.GrossProfit} /></td>
            <td style={{ ...tdStyle, textAlign: 'right', color: '#64748b', fontFamily: 'monospace', fontSize: '0.82rem' }}>
              {r.AvgDaysToDeliver ? `${parseFloat(r.AvgDaysToDeliver).toFixed(0)} d` : '—'}
            </td>
          </TRow>
        ))}
      </tbody>
      <tfoot>
        <tr style={{ background: '#0f172a' }}>
          <td colSpan={4} style={{ ...tdStyle, color: '#94a3b8', fontSize: '0.75rem', padding: '8px 12px' }}>TOTAL</td>
          <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#e2e8f0', fontWeight: '700' }}>{rows.reduce((s,r)=>s+(parseInt(r.JobCount)||0),0)}</td>
          <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#94a3b8', fontWeight: '700' }}>{rows.reduce((s,r)=>s+(parseInt(r.InvoicedCount)||0),0)}</td>
          <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#93c5fd', fontWeight: '700' }}>{fmtNumRaw(rows.reduce((s,r)=>s+(parseFloat(r.TotalSales)||0),0))}</td>
          <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#fca5a5', fontWeight: '700' }}>{fmtNumRaw(rows.reduce((s,r)=>s+(parseFloat(r.TotalCost)||0),0))}</td>
          <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#86efac', fontWeight: '700' }}>{fmtNumRaw(rows.reduce((s,r)=>s+(parseFloat(r.GrossProfit)||0),0))}</td>
          <td />
        </tr>
      </tfoot>
    </table>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// 8. JOB DETAILS
// ─────────────────────────────────────────────────────────────────────────────
const JobDetailsView = ({ data }) => {
  if (!data?.job) return null;
  const { job, containers, cargo, revenue, boe, audit, totals } = data;

  const Section = ({ title, color, children }) => (
    <div style={{ marginBottom: 20 }}>
      <div style={{ background: `linear-gradient(90deg,${color}18,transparent)`, borderLeft: `3px solid ${color}`, padding: '8px 14px', marginBottom: 1 }}>
        <span style={{ fontWeight: '700', fontSize: '0.8rem', color, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: "'Libre Baskerville',Georgia,serif" }}>{title}</span>
      </div>
      <div style={{ border: `1px solid ${color}20`, borderTop: 'none', borderRadius: '0 0 6px 6px', background: '#fff', padding: 14 }}>{children}</div>
    </div>
  );

  const Field = ({ label, value }) => (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 140 }}>
      <span style={{ fontSize: '0.68rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: "'Libre Baskerville',Georgia,serif" }}>{label}</span>
      <span style={{ fontSize: '0.85rem', color: '#1e293b', fontWeight: '500', marginTop: 2, fontFamily: "'Libre Baskerville',Georgia,serif" }}>{value || '—'}</span>
    </div>
  );

  return (
    <div>
      {/* Job header card */}
      <div style={{ background: 'linear-gradient(135deg,#1e293b,#334155)', borderRadius: 10, padding: '16px 20px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontFamily: "'DM Serif Display',Georgia,serif", fontSize: '1.4rem', color: '#f1f5f9' }}>{job.JobCode}</div>
          <div style={{ color: '#94a3b8', fontSize: '0.82rem', marginTop: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>{job.JobType}</span>
            <span style={{ color: '#334155' }}>·</span>
            <ModeTag mode={job.Mode} />
            <span style={{ color: '#334155' }}>·</span>
            <span>{fmtDate(job.JobDate)}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 24 }}>
          <Stat label="Total Sales" value={fmtNumRaw(totals?.sales_home || 0)} color="#93c5fd" />
          <Stat label="Total Cost"  value={fmtNumRaw(totals?.cost || 0)}      color="#fca5a5" />
          <Stat label={totals?.profit >= 0 ? 'Profit' : 'Loss'} value={fmtNumRaw(Math.abs(totals?.profit || 0))} color={totals?.profit >= 0 ? '#6ee7b7' : '#fca5a5'} />
          <div style={{ alignSelf: 'center' }}><StatusTag status={job.IsClosed ? 'Closed' : 'Open'} /></div>
        </div>
      </div>

      {/* Job info */}
      <Section title="Job Information" color="#0369a1">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
          <Field label="Invoice To"       value={job.InvoiceTo} />
          <Field label="Consigner"        value={job.Consigner} />
          <Field label="Consignee"        value={job.Consignee} />
          <Field label="Shipper"          value={job.Shipper} />
          <Field label="Handler"          value={job.HandledBy} />
          <Field label="MBL / MAWB"       value={job.MBL || job.MAWB} />
          <Field label="HBL / HAWB"       value={job.HBL || job.HAWB} />
          <Field label="Vessel / Airline" value={job.Vessel || job.Airline} />
          <Field label="Carrier"          value={job.Carrier} />
          <Field label="Voyage / Flight"  value={job.VoyageNo || job.Flight} />
          <Field label="Load Port"        value={job.LoadPort} />
          <Field label="Dest Port"        value={job.DestPort} />
          <Field label="BL Date"          value={fmtDate(job.BLDate)} />
          <Field label="Sailing Date"     value={fmtDate(job.SailingDate)} />
          <Field label="Arrival Date"     value={fmtDate(job.ArrivalDate)} />
          <Field label="Delivery Date"    value={fmtDate(job.DeliveryDate)} />
          <Field label="Freight"          value={job.Freight} />
          <Field label="Gross Weight"     value={job.GrossWeight ? `${fmtNumRaw(job.GrossWeight, 3)} kg` : null} />
          <Field label="Volume"           value={job.Volume ? `${fmtNumRaw(job.Volume, 3)} cbm` : null} />
          <Field label="Country of Origin" value={job.CountryOfOrigin} />
        </div>
        {job.Remarks && (
          <div style={{ marginTop: 12, padding: '8px 12px', background: '#f8fafc', borderRadius: 6, fontSize: '0.82rem', color: '#475569', fontFamily: "'Libre Baskerville',Georgia,serif" }}>
            <span style={{ fontWeight: '700', color: '#334155' }}>Remarks: </span>{job.Remarks}
          </div>
        )}
      </Section>

      {/* Containers */}
      {containers?.length > 0 && (
        <Section title={`Containers (${containers.length})`} color="#b45309">
          <table style={{ ...tableStyle, boxShadow: 'none' }}>
            <thead>
              <tr style={{ background: '#1e293b' }}>
                {['Container Type','Container No','Seal No','Description'].map(h => (
                  <th key={h} style={{ ...thStyle, cursor: 'default' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {containers.map((c, i) => (
                <TRow key={c.JContainerDetailID} i={i}>
                  <td style={tdStyle}><span style={{ background: '#dbeafe', color: '#1e3a8a', padding: '2px 8px', borderRadius: 8, fontSize: '0.72rem', fontWeight: '700' }}>{c.ContainerType || '—'}</span></td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: '700', color: '#b45309' }}>{c.ContainerNo || '—'}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', color: '#64748b' }}>{c.SealNo || '—'}</td>
                  <td style={{ ...tdStyle, color: '#64748b', fontSize: '0.8rem' }}>{c.Description || '—'}</td>
                </TRow>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {/* Revenue Lines */}
      {revenue?.length > 0 && (
        <Section title={`Revenue Lines (${revenue.length})`} color="#1d4ed8">
          <table style={{ ...tableStyle, boxShadow: 'none' }}>
            <thead>
              <tr style={{ background: '#1e293b' }}>
                {[['Revenue Type','l'],['Code','l'],['Supplier','l'],['Qty','r'],
                  ['Prov Currency','l'],['Prov Foreign','r'],['Sales Currency','l'],
                  ['Sales Home','r'],['Cost','r'],['Tax %','r'],['Tax Amt','r']
                ].map(([h, a]) => (
                  <th key={h} style={{ ...thStyle, cursor: 'default', textAlign: a === 'r' ? 'right' : 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {revenue.map((r, i) => (
                <TRow key={r.InvoiceID} i={i}>
                  <td style={{ ...tdStyle, fontWeight: '600' }}>{r.RevenueType || '—'}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.78rem', color: '#64748b' }}>{r.RevenueCode || r.RTypeCode || '—'}</td>
                  <td style={{ ...tdStyle, fontSize: '0.8rem', color: '#64748b' }}>{r.SupplierName || '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={r.Quantity} /></td>
                  <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#7c3aed' }}>{r.ProvCurrency || '—'} {r.ProvSymbol || ''}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={r.ProvisionForeign} /></td>
                  <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#0369a1' }}>{r.SaleCurrency || '—'} {r.SaleSymbol || ''}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <span style={{ fontFamily: "'Courier Prime','Courier New',monospace", fontWeight: '700', color: '#1d4ed8', fontSize: '0.82rem' }}>{fmtNumRaw(r.SalesHome || 0)}</span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={r.Cost} color="#dc2626" /></td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#64748b', fontFamily: 'monospace', fontSize: '0.78rem' }}>{r.TaxPer || 0}%</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}><AmtCell value={r.TaxAmt} color="#7c3aed" /></td>
                </TRow>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: '#0f172a' }}>
                <td colSpan={7} style={{ ...tdStyle, padding: '8px 12px' }} />
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#93c5fd', fontWeight: '800' }}>{fmtNumRaw(totals?.sales_home || 0)}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#fca5a5', fontWeight: '800' }}>{fmtNumRaw(totals?.cost || 0)}</td>
                <td />
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: '#c4b5fd', fontWeight: '800' }}>{fmtNumRaw(totals?.tax_amt || 0)}</td>
              </tr>
            </tfoot>
          </table>
        </Section>
      )}

      {/* Bill of Entry */}
      {boe?.length > 0 && (
        <Section title={`Bill of Entry (${boe.length})`} color="#0f766e">
          <table style={{ ...tableStyle, boxShadow: 'none' }}>
            <thead>
              <tr style={{ background: '#1e293b' }}>
                {['Bill of Entry No','BOE Date','Shipping Agent'].map(h => (
                  <th key={h} style={{ ...thStyle, cursor: 'default' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {boe.map((b, i) => (
                <TRow key={b.BillOfEntryID} i={i}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: '700', color: '#0f766e' }}>{b.BillOfEntry || '—'}</td>
                  <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#475569' }}>{fmtDate(b.BillofEntryDate)}</td>
                  <td style={tdStyle}>{b.AgentName || '—'}</td>
                </TRow>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {/* Audit Log */}
      {audit?.length > 0 && (
        <Section title={`Audit Log (${audit.length})`} color="#64748b">
          <table style={{ ...tableStyle, boxShadow: 'none' }}>
            <thead>
              <tr style={{ background: '#1e293b' }}>
                {['Date','User','Remarks'].map(h => <th key={h} style={{ ...thStyle, cursor: 'default' }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {audit.map((a, i) => (
                <TRow key={a.JAuditLogID} i={i}>
                  <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#475569', whiteSpace: 'nowrap' }}>{fmtDate(a.TransDate)}</td>
                  <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#64748b' }}>{a.username || a.LoginID || '—'}</td>
                  <td style={{ ...tdStyle, fontSize: '0.82rem', color: '#475569' }}>{a.Remarks || '—'}</td>
                </TRow>
              ))}
            </tbody>
          </table>
        </Section>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT  — structure mirrors FinancialReports exactly
// ─────────────────────────────────────────────────────────────────────────
// 
const today = new Date().toISOString().slice(0, 10);
const monthStart = today.slice(0, 8) + '01';

const JobReports = () => {
  
  const [reportType, setReportType] = useState('job_list');
  const [startDate,  setStartDate]  = useState(monthStart);
  const [endDate,    setEndDate]    = useState(today);
  const [customerId, setCustomerId] = useState('');
  const [jobTypeId,  setJobTypeId]  = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [isClosed,   setIsClosed]   = useState('');
  const [jobId,      setJobId]      = useState('');
  const [fyearId,    setFyearId]    = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [masters,   setMasters]   = useState({});
  const [rows,      setRows]      = useState([]);
  const [summary,   setSummary]   = useState({});
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);

  // Load dropdowns
  useEffect(() => {
    apiClient.get('/JobReports.php?action=dropdowns').then(res => {
      if (res.data.success) {
        const d = res.data.data;
        setMasters(d);
        const cur = (d.years || []).find(y => y.iscurrent === '1' || y.iscurrent === 1);
        if (cur) {
          setFyearId(String(cur.yearid));
          setStartDate(cur.startdate || monthStart);
          setEndDate(cur.enddate < today ? cur.enddate : today);
        }
      }
    }).catch(e => console.error('Dropdown error:', e));
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true); setError(''); setHasLoaded(false);
    const params = new URLSearchParams({
      report_type: reportType,
      start_date:  startDate,
      end_date:    endDate,
      customer_id: customerId || '0',
      job_type_id: jobTypeId  || '0',
      employee_id: employeeId || '0',
      is_closed:   isClosed,
      job_id:      jobId      || '0',
      fyear_id:    fyearId    || '0',
    });
    try {
      const res = await apiClient.get(`/JobReports.php?${params}`);
      if (res.data.success) {
        setRows(res.data.data.rows || []);
        setSummary(res.data.data.summary || {});
        setHasLoaded(true);
        setSearchTerm('');
      } else {
        setError(res.data.message || 'Failed to load report');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error loading report');
    } finally { setLoading(false); }
  }, [reportType, startDate, endDate, customerId, jobTypeId, employeeId, isClosed, jobId, fyearId]);

  const exportCSV = () => {
    if (!rows.length) return;
    const flat = rows.map(r => r.job ? { ...r.job } : r);
    const keys   = Object.keys(flat[0]);
    const header = keys.join(',');
    const body   = flat.map(r => keys.map(k => `"${String(r[k] ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([header + '\n' + body], { type: 'text/csv' }));
    a.download = `${reportType}_${startDate}_${endDate}.csv`;
    a.click();
  };

  const handlePrint = () => {
    const rt = REPORT_TYPES.find(r => r.key === reportType);
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>${rt?.label} — ${masters.comp?.companyname || ''}</title>
    <style>
      body { font-family: Georgia, serif; font-size: 11px; color: #1f2937; margin: 12mm; }
      h1 { font-size: 16px; margin: 0; }
      h2 { font-size: 12px; font-weight: normal; color: #6b7280; margin: 2px 0 8px; }
      table { border-collapse: collapse; width: 100%; margin-top: 8px; }
      th { background: #1e293b; color: #fff; padding: 5px 8px; text-align: left; font-size: 10px; }
      td { padding: 4px 8px; border-bottom: 1px solid #e5e7eb; font-size: 10px; }
      tr:nth-child(even) td { background: #f9fafb; }
      @media print { @page { margin: 10mm; } }
    </style></head><body>
    <h1>${masters.comp?.companyname || 'Job Reports'}</h1>
    <h2>${rt?.label} &nbsp;|&nbsp; ${startDate} to ${endDate}</h2>
    <table><thead><tr>${Object.keys(rows[0] || {}).map(k => `<th>${k}</th>`).join('')}</tr></thead>
    <tbody>${rows.map(r => `<tr>${Object.values(r).map(v => `<td>${v ?? ''}</td>`).join('')}</tr>`).join('')}</tbody></table>
    <script>window.onload=()=>{window.print();}</script></body></html>`);
    win.document.close();
  };

  const rt             = REPORT_TYPES.find(r => r.key === reportType);
  const isJobDetails   = reportType === 'job_details';
  const isStatusReport = reportType === 'job_status';
  const showSearch     = ['job_list','revenue_summary','container_report','cargo_report','shipment_schedule'].includes(reportType);
  const showEmployee   = ['job_list','shipment_schedule'].includes(reportType);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#f8fafc', fontFamily: "'Libre Baskerville', Georgia, serif" }}>

      {/* FONT IMPORT */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Courier+Prime:ital,wght@0,400;0,700;1,400&display=swap');
        @keyframes spin   { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
      `}</style>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        padding: '20px 28px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '3px solid #334155', flexShrink: 0,
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <div style={{ width: 50, height: 50, background: 'rgba(255,255,255,0.08)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.12)' }}>
            <rt.icon size={24} color={rt.color} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: '#94a3b8', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.12em', fontFamily: "'Libre Baskerville',Georgia,serif" }}>
                Job Reports
              </span>
              <span style={{ color: '#475569', fontSize: '0.7rem' }}>›</span>
              <span style={{ color: rt.color, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{rt.label}</span>
            </div>
            <div style={{ fontFamily: "'DM Serif Display',Georgia,serif", color: '#f1f5f9', fontSize: '1.5rem', fontWeight: '400', marginTop: 2 }}>
              {masters.comp?.companyname || 'Job Reports'}
            </div>
          </div>
        </div>
        {hasLoaded && (
          <div style={{ display: 'flex', gap: 24, animation: 'fadeIn .4s ease' }}>
            {summary.total_jobs   != null && <Stat label="Total Jobs"    value={summary.total_jobs}                          color="#e2e8f0" />}
            {summary.open_jobs    != null && <Stat label="Open"          value={summary.open_jobs}                           color="#86efac" />}
            {summary.closed_jobs  != null && <Stat label="Closed"        value={summary.closed_jobs}                         color="#94a3b8" />}
            {summary.total_sales  != null && <Stat label="Total Sales"   value={fmtNumRaw(summary.total_sales)}              color="#93c5fd" />}
            {summary.total_profit != null && <Stat label="Gross Profit"  value={fmtNumRaw(Math.abs(summary.total_profit))}   color={summary.total_profit >= 0 ? '#6ee7b7' : '#fca5a5'} />}
            {summary.sea_jobs     != null && <Stat label="Sea"           value={summary.sea_jobs}                            color="#93c5fd" />}
            {summary.air_jobs     != null && <Stat label="Air"           value={summary.air_jobs}                            color="#fbbf24" />}
            <Stat label="Records" value={summary.total_rows} color="#e2e8f0" />
          </div>
        )}
      </div>

      {/* ── Report Type Tabs ───────────────────────────────────────────────── */}
      <div style={{ background: '#1e293b', padding: '10px 20px', display: 'flex', gap: 4, overflowX: 'auto', flexShrink: 0, borderBottom: '1px solid #334155' }}>
        {REPORT_TYPES.map(r => {
          const active = r.key === reportType;
          const Icon   = r.icon;
          return (
            <button key={r.key}
              onClick={() => { setReportType(r.key); setHasLoaded(false); setSearchTerm(''); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                borderRadius: 7, border: `1px solid ${active ? r.color + '80' : 'transparent'}`,
                cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.8rem',
                fontFamily: "'Libre Baskerville',Georgia,serif",
                fontWeight: active ? '700' : '400',
                background: active ? `${r.color}22` : 'transparent',
                color: active ? r.color : '#94a3b8',
                transition: 'all .15s',
              }}
            >
              <Icon size={13} />{r.label}
            </button>
          );
        })}
      </div>

      {/* ── Filters ───────────────────────────────────────────────────────── */}
      <div style={{ padding: '12px 20px', background: '#fff', borderBottom: '1px solid #e2e8f0', flexShrink: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10, alignItems: 'end' }}>

          {/* Financial Year */}
          <div>
            <label style={lblStyle}>Financial Year</label>
            <select value={fyearId} onChange={e => setFyearId(e.target.value)} style={inpStyle}>
              <option value="">— Current Year —</option>
              {(masters.years || []).map(y => (
                <option key={y.yearid} value={y.yearid}>{y.yearname}</option>
              ))}
            </select>
          </div>

          {/* Date filters — hidden for Job Details */}
          {!isJobDetails && (
            <>
              <div>
                <label style={lblStyle}>From Date</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={inpStyle} />
              </div>
              <div>
                <label style={lblStyle}>To Date</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={inpStyle} />
              </div>
            </>
          )}

          {/* Customer — hidden for Job Details & Status */}
          {!isJobDetails && !isStatusReport && (
            <div>
              <label style={lblStyle}>Customer</label>
              <select value={customerId} onChange={e => setCustomerId(e.target.value)} style={inpStyle}>
                <option value="">All Customers</option>
                {(masters.customers || []).map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Job Type — hidden for Job Details */}
          {!isJobDetails && (
            <div>
              <label style={lblStyle}>Job Type</label>
              <select value={jobTypeId} onChange={e => setJobTypeId(e.target.value)} style={inpStyle}>
                <option value="">All Types</option>
                {(masters.jobtypes || []).map(j => (
                  <option key={j.id} value={j.id}>{j.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Status — hidden for Job Details & Status */}
          {!isJobDetails && !isStatusReport && (
            <div>
              <label style={lblStyle}>Status</label>
              <select value={isClosed} onChange={e => setIsClosed(e.target.value)} style={inpStyle}>
                <option value="">All Status</option>
                <option value="0">Open</option>
                <option value="1">Closed</option>
              </select>
            </div>
          )}

          {/* Employee — only for job_list & shipment_schedule */}
          {showEmployee && (
            <div>
              <label style={lblStyle}>Handler</label>
              <select value={employeeId} onChange={e => setEmployeeId(e.target.value)} style={inpStyle}>
                <option value="">All Handlers</option>
                {(masters.employees || []).map(e => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Job ID — only for job_details */}
          {isJobDetails && (
            <div>
              <label style={lblStyle}>Job ID</label>
              <input
                type="number" placeholder="Enter Job ID…"
                value={jobId} onChange={e => setJobId(e.target.value)}
                style={inpStyle}
              />
            </div>
          )}

          {/* Generate button */}
          <div>
            <label style={lblStyle}>&nbsp;</label>
            <button onClick={loadReport} disabled={loading} style={{
              width: '100%', padding: '9px 14px', borderRadius: 7, border: 'none',
              cursor: loading ? 'not-allowed' : 'pointer',
              background: loading ? '#94a3b8' : `linear-gradient(135deg, ${rt.color}, ${rt.color}cc)`,
              color: '#fff', fontWeight: '700', fontSize: '0.88rem',
              fontFamily: "'Libre Baskerville',Georgia,serif",
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              boxShadow: loading ? 'none' : `0 2px 8px ${rt.color}40`,
              transition: 'all .15s',
            }}>
              {loading ? <><Spinner />Generating…</> : <><RefreshCw size={14} />Generate Report</>}
            </button>
          </div>

        </div>
      </div>

      {/* ── Toolbar ───────────────────────────────────────────────────────── */}
      {hasLoaded && (
        <div style={{ padding: '8px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, animation: 'fadeIn .3s ease' }}>
          <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
            <Calendar size={12} style={{ display: 'inline', marginRight: 4 }} />
            {isJobDetails ? `Job #${jobId}` : `${fmtDate(startDate)} — ${fmtDate(endDate)}`}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            {showSearch && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 7, background: '#fff' }}>
                <Search size={13} color="#94a3b8" />
                <input
                  type="text" placeholder="Search…" value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  style={{ border: 'none', outline: 'none', width: 130, fontSize: '0.82rem', fontFamily: "'Libre Baskerville',Georgia,serif" }}
                />
              </div>
            )}
            <button onClick={exportCSV} style={btnStyle}><Download size={14} /> Export CSV</button>
            <button onClick={handlePrint} style={btnStyle}><Printer size={14} /> Print</button>
          </div>
        </div>
      )}

      {/* ── Error ─────────────────────────────────────────────────────────── */}
      {error && (
        <div style={{ margin: '12px 20px', padding: '10px 14px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', fontSize: '0.88rem', flexShrink: 0 }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* ── Content ───────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>

        {!hasLoaded && !loading && (
          <div style={{ textAlign: 'center', padding: '80px 20px', color: '#94a3b8', animation: 'fadeIn .5s ease' }}>
            <rt.icon size={52} style={{ margin: '0 auto 16px', opacity: .2, display: 'block' }} />
            <p style={{ fontFamily: "'DM Serif Display',Georgia,serif", fontSize: '1.2rem', margin: '0 0 6px', color: '#64748b' }}>
              {rt.label}
            </p>
            <p style={{ margin: 0, fontSize: '0.85rem' }}>Configure filters above and click <strong>Generate Report</strong></p>
          </div>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: '80px', color: rt.color }}>
            <div style={{ width: 36, height: 36, border: `3px solid ${rt.color}30`, borderTopColor: rt.color, borderRadius: '50%', animation: 'spin .7s linear infinite', margin: '0 auto 16px' }} />
            <p style={{ fontFamily: "'DM Serif Display',Georgia,serif", fontSize: '1rem', margin: 0 }}>Preparing {rt.label}…</p>
          </div>
        )}

        {hasLoaded && !loading && rows.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
            <BookOpen size={40} style={{ margin: '0 auto 10px', opacity: .3, display: 'block' }} />
            <p style={{ margin: 0 }}>No data found for the selected filters.</p>
          </div>
        )}

        {hasLoaded && !loading && rows.length > 0 && (
          <div style={{ animation: 'fadeIn .4s ease' }}>
            {reportType === 'job_list'          && <JobListTable          rows={rows} searchTerm={searchTerm} />}
            {reportType === 'revenue_summary'   && <RevenueSummaryTable   rows={rows} summary={summary} searchTerm={searchTerm} />}
            {reportType === 'revenue_by_type'   && <RevenueByTypeTable    rows={rows} />}
            {reportType === 'container_report'  && <ContainerTable        rows={rows} searchTerm={searchTerm} />}
            {reportType === 'cargo_report'      && <CargoTable            rows={rows} searchTerm={searchTerm} />}
            {reportType === 'shipment_schedule' && <ShipmentScheduleTable rows={rows} searchTerm={searchTerm} />}
            {reportType === 'job_status'        && <JobStatusTable        rows={rows} />}
            {reportType === 'job_details'       && <JobDetailsView        data={rows[0]} />}
          </div>
        )}

      </div>
    </div>
  );
};

export default JobReports;